import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_SHORT,
  COMPANY_TAGLINE,
  MAP_URL,
  COMMUNITY_FOCUS,
  PILLARS,
  PUBLIC_INFO,
} from './constants';

function MandalaMark({ className = 'h-10 w-10' }) {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" className={className}>
      <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="20" cy="20" r="11" fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx="20" cy="20" r="4" fill="currentColor" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <line
          key={angle}
          x1="20"
          y1="20"
          x2="20"
          y2="2"
          stroke="currentColor"
          strokeWidth="1"
          transform={`rotate(${angle} 20 20)`}
        />
      ))}
    </svg>
  );
}

function PillarIcon({ name, className = 'h-6 w-6' }) {
  const stroke = 'currentColor';
  const common = { fill: 'none', stroke, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'heritage') {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M3 20h18M5 20V10l7-5 7 5v10M9 20v-6h6v6" {...common} />
      </svg>
    );
  }
  if (name === 'community') {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <circle cx="9" cy="9" r="3" {...common} />
        <circle cx="17" cy="11" r="2.4" {...common} />
        <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5" {...common} />
      </svg>
    );
  }
  if (name === 'service') {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M12 3l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.6 6.7 19.2l1.1-5.9L3.5 9.2l5.9-.8L12 3z" {...common} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M12 21s-7-6.5-7-12a7 7 0 1 1 14 0c0 5.5-7 12-7 12z" {...common} />
      <circle cx="12" cy="9.5" r="2.4" {...common} />
    </svg>
  );
}

function Header() {
  const navItems = [
    { label: 'Heritage', href: '#heritage' },
    { label: 'Community', href: '#community' },
    { label: 'Address', href: '#address' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-ivory/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex items-center gap-3 text-maroon" aria-label={`${COMPANY_SHORT} home`}>
          <MandalaMark className="h-9 w-9 text-maroon" />
          <div className="leading-tight">
            <p className="font-display text-lg font-bold text-maroon">{COMPANY_SHORT}</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-saffronDeep">{COMPANY_TAGLINE}</p>
          </div>
        </a>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-4 py-2 text-sm font-semibold text-ink/75 transition hover:bg-sandalwood hover:text-maroon"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#address"
          className="hidden rounded-full bg-maroon px-4 py-2 text-sm font-bold text-ivory transition hover:bg-maroonDeep sm:inline-flex"
        >
          View address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-hero-gradient pb-32 pt-20 text-ivory sm:pb-40 sm:pt-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(255,248,231,0.35), transparent 38%), radial-gradient(circle at 80% 70%, rgba(212,160,23,0.35), transparent 42%)',
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-5xl px-5 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-ivory/35 bg-ivory/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.26em] text-ivory">
          <MandalaMark className="h-3.5 w-3.5 text-marigold" />
          Public Community Profile
        </span>
        <h1 className="font-display mt-7 text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          {COMPANY_NAME}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-ivory/85">
          A restrained public profile honoring the Biswakarma tradition — craft, devotion, and family — and the
          community gathered around it across the United States.
        </p>
      </div>

      <div className="relative mx-auto -mb-24 mt-12 max-w-5xl px-5 sm:-mb-32">
        <div className="grid gap-6 rounded-3xl border border-line bg-ivory p-7 shadow-soft md:grid-cols-[1.1fr_0.9fr] md:p-9">
          <div className="border-b border-line pb-6 md:border-b-0 md:border-r md:pb-0 md:pr-9">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">Organization</p>
            <p className="font-display mt-3 text-3xl font-bold leading-tight text-maroon">{COMPANY_NAME}</p>
            <p className="mt-4 text-sm leading-7 text-muted">
              A community organization presented for the public record — names, location, and correspondence address
              kept visible; internal identifiers kept private.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#community"
                className="rounded-full bg-maroon px-5 py-3 text-sm font-bold text-ivory transition hover:bg-maroonDeep"
              >
                Community focus
              </a>
              <a
                href="#address"
                className="rounded-full border border-maroon/30 px-5 py-3 text-sm font-bold text-maroon transition hover:bg-sandalwood"
              >
                View address
              </a>
            </div>
          </div>
          <div className="flex flex-col justify-between gap-6 md:pl-2">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">Postal Address</p>
              <p className="font-display mt-3 text-xl font-bold leading-snug text-ink">{COMPANY_ADDRESS_FULL}</p>
            </div>
            <div className="rounded-2xl bg-sandalwood/70 p-5">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-maroon">Tagline</p>
              <p className="font-display mt-2 text-2xl font-bold text-maroon">{COMPANY_TAGLINE}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PillarsGrid() {
  return (
    <section id="heritage" className="bg-ivory pt-32 pb-20 sm:pt-40">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">Public Profile Pillars</p>
          <h2 className="font-display mt-3 text-4xl font-bold text-maroon sm:text-5xl">Four steady pillars</h2>
          <p className="mt-4 leading-7 text-muted">
            Heritage, community, service, and place — the four pillars hold the public profile together without making
            unsupported operational claims.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {PILLARS.map((pillar) => (
            <article
              key={pillar.label}
              className="group relative overflow-hidden rounded-3xl border border-line bg-surface p-7 shadow-card transition hover:-translate-y-0.5 hover:shadow-soft"
            >
              <div className="flex items-start justify-between">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-sandalwood text-maroon">
                  <PillarIcon name={pillar.icon} />
                </div>
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">{pillar.label}</p>
              </div>
              <p className="font-display mt-6 text-2xl font-bold text-ink">{pillar.value}</p>
              <p className="mt-3 text-sm leading-7 text-muted">{pillar.note}</p>
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-marigold/10 transition group-hover:bg-marigold/20" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CommunityTimeline() {
  return (
    <section id="community" className="bg-sandalwood/40 py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">Community Focus</p>
            <h2 className="font-display mt-3 text-4xl font-bold text-maroon sm:text-5xl">
              A measured, heritage-led profile
            </h2>
            <p className="mt-4 leading-7 text-muted">
              The page borrows from cultural editorial systems: warm institutional color, formal serif headings, and a
              vertical reading order instead of generic marketing cards.
            </p>
            <div className="mt-8 grid gap-3">
              {PUBLIC_INFO.map((item) => (
                <div key={item.label} className="rounded-2xl border border-line bg-surface px-5 py-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.24em] text-saffronDeep">{item.label}</p>
                  <p className="mt-1 font-display text-lg font-bold text-ink">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <ol className="relative space-y-8 border-l-2 border-dashed border-maroon/30 pl-8">
            {COMMUNITY_FOCUS.map((focus) => (
              <li key={focus.title} className="relative">
                <span
                  aria-hidden="true"
                  className="absolute -left-[42px] top-1 grid h-9 w-9 place-items-center rounded-full bg-maroon font-display text-sm font-bold text-ivory shadow-card"
                >
                  {focus.step}
                </span>
                <article className="rounded-3xl border border-line bg-surface p-7 shadow-card">
                  <h3 className="font-display text-2xl font-bold text-maroon">{focus.title}</h3>
                  <p className="mt-3 leading-7 text-muted">{focus.body}</p>
                </article>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function AddressBand() {
  return (
    <section id="address" className="relative bg-maroon py-20 text-ivory">
      <div
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 20%, rgba(232,119,34,0.5), transparent 35%), radial-gradient(circle at 85% 80%, rgba(212,160,23,0.4), transparent 38%)',
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-3xl px-5 text-center">
        <MandalaMark className="mx-auto h-10 w-10 text-marigold" />
        <p className="mt-5 text-[11px] font-black uppercase tracking-[0.28em] text-marigold">Postal Address</p>
        <h2 className="font-display mt-3 text-4xl font-bold sm:text-5xl">{COMPANY_LOCATION}</h2>
        <p className="mt-5 leading-7 text-ivory/80">
          Use the supplied address below for public profile, directory listing, and correspondence reference.
        </p>
        <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-marigold/30 bg-ivory/10 p-7 backdrop-blur">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-marigold">Organization Address</p>
          <p className="font-display mt-3 text-2xl font-bold leading-snug">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-marigold px-6 py-3 text-sm font-bold text-maroon transition hover:bg-ivory"
          >
            Open map
            <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
              <path d="M5 10h10M11 6l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-maroonDeep text-ivory/75">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 py-10 text-center text-sm">
        <MandalaMark className="h-8 w-8 text-marigold" />
        <p className="font-display text-xl font-bold text-ivory">{COMPANY_NAME}</p>
        <p>{COMPANY_LOCATION}</p>
        <p className="text-xs text-ivory/55">&copy; {new Date().getFullYear()} {COMPANY_SHORT}. All rights reserved.</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-ivory text-ink">
      <a
        href="#heritage"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-maroon focus:px-3 focus:py-2 focus:text-ivory"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <PillarsGrid />
        <CommunityTimeline />
        <AddressBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
